import { t } from "@lingui/macro";
import { FileText, Mail, MessageSquare, Target, TrendingUp, Wand2 } from "lucide-react";

import { Seo } from "@/client/components/seo";

import { CtaBand, FeatureCard, PageHero, Section } from "../_components/page-shell";

type Guide = {
  icon: typeof FileText;
  title: string;
  description: string;
};

export const GuidesPage = () => {
  const guides: Guide[] = [
    {
      icon: FileText,
      title: t`Beat the ATS: format your resume right`,
      description: t`Learn the formatting rules that keep applicant tracking systems from rejecting your resume before a recruiter ever sees it.`,
    },
    {
      icon: Target,
      title: t`Write bullet points that get interviews`,
      description: t`Turn flat job duties into results-driven achievements that show your impact and grab a hiring manager's attention.`,
    },
    {
      icon: Wand2,
      title: t`Tailor your resume to each job`,
      description: t`Match your experience to every posting by mirroring the right keywords and highlighting the skills each role needs.`,
    },
    {
      icon: Mail,
      title: t`Craft a cover letter in minutes`,
      description: t`Build a focused, personal cover letter that complements your resume and speaks directly to the company you want to join.`,
    },
    {
      icon: MessageSquare,
      title: t`Prepare for the interview`,
      description: t`Anticipate the questions you will face, structure your answers, and walk into every interview with confidence.`,
    },
    {
      icon: TrendingUp,
      title: t`Negotiate your offer`,
      description: t`Understand your worth and use clear, professional tactics to negotiate salary, benefits, and terms with ease.`,
    },
  ];

  return (
    <>
      <Seo
        title={t`Career guides for your job search`}
        description={t`Practical, no-nonsense guides on writing your resume, acing interviews and landing the job you want.`}
        path="/guides"
        breadcrumb={t`Career Guides`}
      />

      <PageHero
        eyebrow={t`Career Guides`}
        icon={FileText}
        title={t`Guides to move your job search forward`}
        subtitle={t`Practical, no-nonsense advice on writing your resume, acing interviews, and landing the job you want.`}
      />

      <Section>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {guides.map((guide, index) => (
            <FeatureCard
              key={guide.title}
              icon={guide.icon}
              title={guide.title}
              description={guide.description}
              index={index}
            />
          ))}
        </div>
      </Section>

      <CtaBand
        title={t`Ready to put these guides into practice?`}
        description={t`Create your free Izicvs account and build a resume that gets you interviews.`}
        ctaLabel={t`Get started for free`}
      />
    </>
  );
};
